<template>
  <div class="row-block">
    <div class="date-selectors">
      <button @click="selectWeek()" :class="{ 'active': isWeekSelected }" :disabled="loading">
        {{ $tr('analytics.filters.week') }}
      </button>
      <button @click="selectMonth()" :class="{ 'active': isMonthSelected }" :disabled="loading">
        {{ $tr('analytics.filters.month') }}
      </button>
      <button @click="selectYear()" :class="{ 'active': isYearSelected }" :disabled="loading">
        {{ $tr('analytics.filters.year') }}
      </button>
    </div>

    <div class="date-picker">
      <div class="input-group" v-if="dateFormat">
        <input-dates
          :inputProp="inputDate"
          :dateFormat="dateFormat"
          @inputChange="inputChange"
          :checkDatesDisabledFunc="checkDatesDisabled"
          :loading="loading"
        />
        <label class="helper" v-if="errors.has('dateField')"> {{ errors.first('dateField') }} </label>
        <img
          :disabled="loading"
          @click.prevent.stop="showPicker = !showPicker"
          src="/static/images/elements/analytics/calendar.svg"
          alt="Calendar icon"
        >
      </div>


      <date-range
        v-show="showPicker"
        class="analytics-calendar calendar dropdown"
        :sync-range.sync="range"
        :lang="currentLocale"
        :emitChangeOnStep0="true"
        v-on-click-away="hideDatepicker"
        :disabled-func="checkDatesDisabled"
        :days-disabled-end="disabled_date"
        :monthYearFormat="dateFormatPicker"
        :first-day-of-week="1"
      />
    </div>

  </div>
</template>

<script>
// Sized 400kb, need to replace
import { DateRange } from 'vue-date-range'
import { directive as VueClickAway } from 'vue-clickaway'
import  InputDates from './InputDates.vue';

import { mapMutations } from 'vuex'

import Moment from 'moment'

const MAX_DATE = Moment()
const MIN_DATE = Moment().subtract(8, 'years')

export default {
  name: 'analytics-filters-datepicker',

  directives: {
    onClickAway: VueClickAway
  },

  components: {
    DateRange,
    InputDates
  },

  props: {
    loading: Boolean
  },

  data () {
    return {
      showPicker: false,
      maxDate: MAX_DATE,
      minDate: MIN_DATE
    }
  },

  computed: {
    disabled_date() { return Moment('2018-01-01') },
    currentLocale () {
      return this.$store.state.locale.value
    },

    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    },

    dateFormat() {
      return this.is_japan_locale ? 'YYYY.MM.DD' : 'DD.MM.YYYY';
    },

    dateFormatPicker() {
      return this.is_japan_locale ? 'YYYY - MM' : 'MM - YYYY';
    },

    startDate: {
      get () {
        let sd = this.$store.getters['analytics/createdAfter']
        if (sd.diff === undefined) {
          return Moment(sd)
        }
        return sd
      },

      set (val) {
        this.filtersChange({createdAfter: val})
      }
    },

    endDate: {
      get () {
        let ed = this.$store.getters['analytics/createdBefore']
        if (ed.diff === undefined) {
          return Moment(ed)
        }
        return ed
      },

      set (val) { this.filtersChange({createdBefore: val}) }
    },

    inputDate: {
      get () {
        return this.startDate.format(this.dateFormat) + '-' + this.endDate.format(this.dateFormat)
      },

      set (val) {
        const [startDate, endDate] = val.split('-')

        if (Moment(startDate, this.dateFormat).diff(this.startDate) !== 0) {
          this.startDate = Moment(startDate, this.dateFormat).startOf('day')
        }

        if (Moment(endDate, this.dateFormat).diff(this.endDate) !== 0) {
          this.endDate = Moment(endDate, this.dateFormat).startOf('day')
        }
      }
    },

    range: {
      get () {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        }
      },

      set (val) {
        if (Moment(val.startDate, this.dateFormat).isValid()) {
          this.startDate = Moment(val.startDate, this.dateFormat).startOf('day')
        }

        if (Moment(val.endDate, this.dateFormat).isValid()) {
          this.endDate = Moment(val.endDate, this.dateFormat).startOf('day')
        }
      }
    },

    isWeekSelected () {
      return this.startDate.diff(Moment().startOf('day').subtract(7, 'days')) === 0 &&
             this.endDate.diff(Moment().startOf('day')) === 0
    },

    isMonthSelected () {
      return this.startDate.diff(Moment().startOf('day').subtract(1, 'month')) === 0 &&
             this.endDate.diff(Moment().startOf('day')) === 0
    },

    isYearSelected () {
      return this.startDate.diff(Moment().startOf('day').subtract(1, 'year')) === 0 &&
             this.endDate.diff(Moment().startOf('day')) === 0
    }
  },

  methods: {
    checkDatesDisabled (dayMoment) {
      if (dayMoment.diff(this.maxDate) >= 0) {
        return true
      }

      if (dayMoment.diff(this.minDate) < 0) {
        return true
      }

      return false
    },

    selectWeek () {
      this.startDate = Moment().startOf('day').subtract(7, 'days')
      this.endDate = Moment().startOf('day')
    },

    selectMonth () {
      this.startDate = Moment().startOf('day').subtract(1, 'month')
      this.endDate = Moment().startOf('day')
    },

    selectYear () {
      this.startDate = Moment().startOf('day').subtract(1, 'year')
      this.endDate = Moment().startOf('day')
    },

    hideDatepicker () {
      if (!this.showPicker) {
        return
      }

      this.showPicker = false
    },

    inputChange (val) {
      this.inputDate = val
    },

    ...mapMutations('analytics', [
      'filtersChange'
    ])
  }
}
</script>

<style lang="scss" scoped>
  @import '../../../styles/colors.scss';

  .row-block {
    position: relative;
  }

  .date-selectors {
    display: flex;

    button {
      // // font-family: 'Roboto', sans-serif;
      font-size: 15px;
      line-height: 22px;
      font-weight: normal;
      background: white;
      border: 1px solid #D2D6E0;
      min-width: 80px;
      padding: 8px 0;
      box-sizing: border-box;

      &:first-child { border-radius: 2px 0 0 2px; }
      &:last-child { border-radius: 0 2px 2px 0; }
      &:not(:first-child) { border-left: none; }

      &:hover:not(.active) { cursor: pointer; }
      &.active {
        background: #F4F5F7;
        pointer-events: none;
      }
    }
  }

  .date-picker {
    margin-left: 12px;

    .input-group {
      display: flex;
      align-items: center;
      position: relative;

      img {
        position: absolute;
        right: 12.5px;

        /* fu***ng IE11 */
        top: 50%;
        transform: translateY(-50%);

        &:hover { cursor: pointer; }
      }
    }


    .calendar {
      position: absolute;
      top: calc(100% + 5px);
      right: 0;
    }
  }

</style>
